import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  TextInput,
  ActivityIndicator,
  FlatList,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { BarChart3, Download } from 'lucide-react-native';
import { supabase } from '@/utils/supabase';
import AdminHeader from '@/components/admin/AdminHeader';

type TabKey = 'sessions' | 'selections' | 'courses';

const BG = '#050B17';
const CARD = 'rgba(255,255,255,0.06)';
const BORDER = 'rgba(255,255,255,0.10)';
const TEXT = '#F2EBDD';
const MUTED = '#9AA7B8';
const PURPLE = '#C8531E';

const PAGE_SIZE = 25;

function formatTs(ts?: string | null) {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleString('he-IL', { 
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

async function safeCount(table: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from(table)
      .select('*', { count: 'exact', head: true });
    
    if (error) {
      console.error(`Error counting ${table}:`, error);
      return 0;
    }
    
    return count ?? 0;
  } catch (e) {
    console.error(`Exception counting ${table}:`, e);
    return 0;
  }
}

export default function AdminDashboard() {
  const router = useRouter();
  const [tab, setTab] = useState<TabKey>('sessions');

  const [loadingTop, setLoadingTop] = useState(true);
  const [loadingList, setLoadingList] = useState(true);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [kpis, setKpis] = useState({
    sessions: 0,
    selections: 0,
    courses: 0,
  });

  const [rows, setRows] = useState<any[]>([]);
  const [errorText, setErrorText] = useState<string | null>(null);

  // Check admin authentication
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.user) {
          router.replace('/admin/login');
          return;
        }

        const { data: adminRow, error } = await supabase
          .from('admins')
          .select('id, is_active, role')
          .eq('user_id', session.user.id)
          .eq('is_active', true)
          .maybeSingle();

        if (error || !adminRow) {
          console.error('Admin check failed:', error);
          await supabase.auth.signOut();
          router.replace('/admin/login');
          return;
        }

        setCheckingAuth(false);
      } catch (e) {
        console.error('Error checking admin:', e);
        router.replace('/admin/login');
      }
    };

    checkAdmin();
  }, [router]);

  const title = useMemo(() => {
    if (tab === 'sessions') return 'Chat Sessions';
    if (tab === 'selections') return 'Course Selections';
    return 'Processed Courses';
  }, [tab]);

  const loadTop = useCallback(async () => {
    setLoadingTop(true);
    
    try {
      const [sessions, selections, courses] = await Promise.all([
        safeCount('chat_sessions'),
        safeCount('course_selections'),
        safeCount('processed_raw_courses'),
      ]);
      
      setKpis({ sessions, selections, courses });
    } catch (e) {
      console.error('Error loading KPIs:', e);
    } finally {
      setLoadingTop(false);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    setErrorText(null);

    try {
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const query = q.trim();

      if (tab === 'sessions') {
        let req = supabase
          .from('chat_sessions')
          .select('id,user_session_id,started_at,first_message,source_page,initial_topic,referrer')
          .order('started_at', { ascending: false })
          .range(from, to);

        if (query) {
          req = req.or(
            [
              `user_session_id.ilike.%${query}%`,
              `first_message.ilike.%${query}%`,
              `source_page.ilike.%${query}%`,
              `initial_topic.ilike.%${query}%`,
              `referrer.ilike.%${query}%`,
            ].join(',')
          );
        }

        const { data, error } = await req;
        if (error) throw error;
        
        setRows(data || []);
        setHasMore((data?.length ?? 0) === PAGE_SIZE);
      } else if (tab === 'selections') {
        let req = supabase
          .from('course_selections')
          .select(
            'id,created_at,session_id,course_name,course_id,topic,sub_topic,position,total_courses_shown,mentor_name,conversation_id,user_id'
          )
          .order('created_at', { ascending: false })
          .range(from, to);

        if (query) {
          req = req.or(
            [
              `course_name.ilike.%${query}%`,
              `course_id.ilike.%${query}%`,
              `topic.ilike.%${query}%`,
              `sub_topic.ilike.%${query}%`,
              `mentor_name.ilike.%${query}%`,
              `session_id.ilike.%${query}%`,
            ].join(',')
          );
        }

        const { data, error } = await req;
        if (error) throw error;
        
        setRows(data || []);
        setHasMore((data?.length ?? 0) === PAGE_SIZE);
      } else {
        let req = supabase
          .from('processed_raw_courses')
          .select(
            'id,course_id,title,provider,topic,format,level,price,duration,language,has_voucher,voucher_text,summary'
          )
          .order('id', { ascending: false })
          .range(from, to);

        if (query) {
          req = req.or(
            [
              `title.ilike.%${query}%`,
              `provider.ilike.%${query}%`,
              `topic.ilike.%${query}%`,
              `course_id.ilike.%${query}%`,
              `voucher_text.ilike.%${query}%`,
            ].join(',')
          );
        }

        const { data, error } = await req;
        if (error) throw error;
        
        setRows(data || []);
        setHasMore((data?.length ?? 0) === PAGE_SIZE);
      }
    } catch (e: any) {
      console.error('Error loading list:', e);
      setRows([]);
      setErrorText(e?.message ?? 'שגיאה בטעינה');
    } finally {
      setLoadingList(false);
    }
  }, [tab, q, page]);

  useEffect(() => {
    loadTop();
  }, [loadTop]);

  useEffect(() => {
    setPage(0);
    setHasMore(true);
  }, [tab, q]);

  useEffect(() => {
    if (!checkingAuth) {
      loadList();
    }
  }, [loadList, checkingAuth]);

  const nextPage = () => {
    if (hasMore) {
      setPage((p) => p + 1);
    }
  };
  
  const prevPage = () => setPage((p) => Math.max(0, p - 1));

  const TabButton = ({ k, label }: { k: TabKey; label: string }) => {
    const active = tab === k;
    return (
      <Pressable onPress={() => setTab(k)} style={[styles.tabBtn, active && styles.tabBtnActive]}>
        <Text style={[styles.tabText, active && styles.tabTextActive]}>{label}</Text>
      </Pressable>
    );
  };

  const HeaderRow = () => {
    if (tab === 'sessions') {
      return (
        <View style={styles.tableHeader}>
          <Text style={[styles.th, { flex: 1.1 }]}>תאריך</Text>
          <Text style={[styles.th, { flex: 1.1 }]}>source</Text>
          <Text style={[styles.th, { flex: 1.1 }]}>topic</Text>
          <Text style={[styles.th, { flex: 2.2 }]}>first message</Text>
        </View>
      );
    }
    if (tab === 'selections') {
      return (
        <View style={styles.tableHeader}>
          <Text style={[styles.th, { flex: 1.1 }]}>תאריך</Text>
          <Text style={[styles.th, { flex: 2.2 }]}>קורס</Text>
          <Text style={[styles.th, { flex: 1.2 }]}>topic</Text>
          <Text style={[styles.th, { flex: 0.7 }]}>pos</Text>
        </View>
      );
    }
    return (
      <View style={styles.tableHeader}>
        <Text style={[styles.th, { flex: 2.2 }]}>כותרת</Text>
        <Text style={[styles.th, { flex: 1.2 }]}>ספק</Text>
        <Text style={[styles.th, { flex: 1.2 }]}>topic</Text>
        <Text style={[styles.th, { flex: 0.8 }]}>voucher</Text>
      </View>
    );
  };

  const Row = ({ item }: { item: any }) => {
    if (tab === 'sessions') {
      return (
        <View style={styles.tr}>
          <Text style={[styles.td, { flex: 1.1 }]} numberOfLines={1}>
            {formatTs(item?.started_at)}
          </Text>
          <Text style={[styles.td, { flex: 1.1 }]} numberOfLines={1}>
            {item?.source_page ?? '—'}
          </Text>
          <Text style={[styles.td, { flex: 1.1 }]} numberOfLines={1}>
            {item?.initial_topic ?? '—'}
          </Text>
          <Text style={[styles.td, { flex: 2.2 }]} numberOfLines={2}>
            {item?.first_message ?? '—'}
          </Text>
        </View>
      );
    }

    if (tab === 'selections') {
      return (
        <View style={styles.tr}>
          <Text style={[styles.td, { flex: 1.1 }]} numberOfLines={1}>
            {formatTs(item?.created_at)}
          </Text>
          <View style={[styles.tdWrap, { flex: 2.2 }]}>
            <Text style={styles.tdStrong} numberOfLines={2}>
              {item?.course_name ?? '—'}
            </Text>
            <Text style={styles.tdMuted} numberOfLines={1}>
              {item?.mentor_name ? `מכללה: ${item.mentor_name}` : ' '}
            </Text>
          </View>
          <Text style={[styles.td, { flex: 1.2 }]} numberOfLines={1}>
            {item?.topic ?? '—'}
          </Text>
          <Text style={[styles.td, { flex: 0.7 }]} numberOfLines={1}>
            {typeof item?.position === 'number'
              ? `${item.position + 1}/${item?.total_courses_shown ?? '—'}`
              : '—'}
          </Text>
        </View>
      );
    }

    const hasVoucher = !!item?.has_voucher;
    return (
      <View style={styles.tr}>
        <View style={[styles.tdWrap, { flex: 2.2 }]}>
          <Text style={styles.tdStrong} numberOfLines={2}>
            {item?.title ?? '—'}
          </Text>
          <Text style={styles.tdMuted} numberOfLines={1}>
            {item?.course_id ? `ID: ${item.course_id}` : ' '}
          </Text>
        </View>
        <Text style={[styles.td, { flex: 1.2 }]} numberOfLines={1}>
          {item?.provider ?? '—'}
        </Text>
        <Text style={[styles.td, { flex: 1.2 }]} numberOfLines={1}>
          {item?.topic ?? '—'}
        </Text>
        <View style={[styles.tdWrap, { flex: 0.8, alignItems: 'flex-end' }]}>
          <View style={[styles.badge, hasVoucher ? styles.badgeOn : styles.badgeOff]}>
            <Text style={styles.badgeText}>{hasVoucher ? 'כן' : 'לא'}</Text>
          </View>
          {!!item?.voucher_text && (
            <Text style={styles.tdMuted} numberOfLines={1}>
              {item.voucher_text}
            </Text>
          )}
        </View>
      </View>
    );
  };

  if (checkingAuth) {
    return (
      <View style={[styles.page, { justifyContent: 'center', alignItems: 'center' }]}>
        <ActivityIndicator color={PURPLE} size="large" />
        <Text style={[styles.loadingText, { marginTop: 16 }]}>בודק הרשאות...</Text>
      </View>
    );
  }

  return (
    <View style={styles.page}>
      <AdminHeader
        title="Admin Dashboard"
        subtitle={title}
        rightActions={
          <>
            <Pressable
              onPress={() => router.push('/admin/dashboard')}
              style={({ pressed }) => [styles.actionBtn, pressed && styles.actionBtnPressed]}
            >
              <BarChart3 size={16} color={PURPLE} />
              <Text style={styles.actionBtnText}>סטטיסטיקות</Text>
            </Pressable>

            <Pressable
              onPress={() => router.push('/admin/export')}
              style={({ pressed }) => [styles.actionBtn, pressed && styles.actionBtnPressed]}
            >
              <Download size={16} color="#10b981" />
              <Text style={styles.actionBtnText}>ייצוא</Text>
            </Pressable>
          </>
        }
      />

      {/* KPIs */}
      <View style={styles.kpiRow}>
        <View style={styles.kpiCard}>
          <Text style={styles.kpiLabel}>Sessions</Text>
          {loadingTop ? (
            <ActivityIndicator color={PURPLE} />
          ) : (
            <Text style={styles.kpiValue}>{kpis.sessions.toLocaleString()}</Text>
          )}
        </View>
        <View style={styles.kpiCard}>
          <Text style={styles.kpiLabel}>Selections</Text>
          {loadingTop ? (
            <ActivityIndicator color={PURPLE} />
          ) : (
            <Text style={styles.kpiValue}>{kpis.selections.toLocaleString()}</Text>
          )}
        </View>
        <View style={styles.kpiCard}>
          <Text style={styles.kpiLabel}>Courses</Text>
          {loadingTop ? (
            <ActivityIndicator color={PURPLE} />
          ) : (
            <Text style={styles.kpiValue}>{kpis.courses.toLocaleString()}</Text>
          )}
        </View>
      </View>

      {/* Tabs */}
      <View style={styles.tabs}>
        <TabButton k="sessions" label="Sessions" />
        <TabButton k="selections" label="Selections" />
        <TabButton k="courses" label="Courses" />
      </View>

      {/* Search + pagination */}
      <View style={styles.controls}>
        <TextInput
          value={q}
          onChangeText={setQ}
          placeholder="חיפוש..."
          placeholderTextColor="rgba(255,255,255,0.35)"
          style={styles.search}
        />

        <View style={styles.pager}>
          <Pressable
            onPress={prevPage}
            disabled={page === 0}
            style={[styles.pagerBtn, page === 0 && styles.pagerBtnDis]}
          >
            <Text style={styles.pagerText}>הקודם</Text>
          </Pressable>
          <Text style={styles.pagerMid}>עמוד {page + 1}</Text>
          <Pressable
            onPress={nextPage}
            disabled={!hasMore}
            style={[styles.pagerBtn, !hasMore && styles.pagerBtnDis]}
          >
            <Text style={styles.pagerText}>הבא</Text>
          </Pressable>
        </View>
      </View>

      {/* Table */}
      <View style={styles.table}>
        <HeaderRow />

        {loadingList ? (
          <View style={styles.loadingBox}>
            <ActivityIndicator color={PURPLE} />
            <Text style={styles.loadingText}>טוען…</Text>
          </View>
        ) : errorText ? (
          <View style={styles.loadingBox}>
            <Text style={styles.errTitle}>שגיאה</Text>
            <Text style={styles.errText}>{errorText}</Text>
          </View>
        ) : rows.length === 0 ? (
          <View style={styles.loadingBox}>
            <Text style={styles.loadingText}>אין תוצאות</Text>
          </View>
        ) : (
          <FlatList
            data={rows}
            keyExtractor={(item) => String(item?.id ?? Math.random())}
            renderItem={({ item }) => <Row item={item} />}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ paddingBottom: 24 }}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: BG, padding: 14, gap: 12 },

  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: BORDER,
  },
  actionBtnPressed: {
    transform: [{ scale: 0.95 }],
    opacity: 0.8,
  },
  actionBtnText: {
    fontWeight: '900',
    fontSize: 12,
    color: TEXT,
  },

  kpiRow: { flexDirection: 'row', gap: 10 },
  kpiCard: {
    flex: 1,
    padding: 12,
    borderRadius: 0,
    backgroundColor: CARD,
    borderWidth: 1,
    borderColor: BORDER,
    alignItems: 'flex-end',
    gap: 6,
  },
  kpiLabel: { color: MUTED, fontSize: 12, fontWeight: '800' },
  kpiValue: { color: TEXT, fontSize: 22, fontWeight: '900' },

  tabs: {
    flexDirection: 'row',
    gap: 8,
    padding: 6,
    borderRadius: 0,
    backgroundColor: CARD,
    borderWidth: 1,
    borderColor: BORDER,
  },
  tabBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'transparent',
  },
  tabBtnActive: {
    backgroundColor: 'rgba(200,83,30,0.18)',
    borderColor: 'rgba(200,83,30,0.18)',
  },
  tabText: { color: MUTED, fontWeight: '900', fontSize: 12 },
  tabTextActive: { color: TEXT },

  controls: {
    gap: 10,
    padding: 12,
    borderRadius: 0,
    backgroundColor: CARD,
    borderWidth: 1,
    borderColor: BORDER,
  },

  search: {
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: TEXT,
    textAlign: 'right',
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}),
  } as any,

  pager: { flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'space-between' },
  pagerBtn: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  pagerBtnDis: { opacity: 0.4 },
  pagerText: { color: TEXT, fontWeight: '900', fontSize: 13 },
  pagerMid: { color: MUTED, fontWeight: '800', fontSize: 13 },

  table: {
    flex: 1,
    borderRadius: 0,
    backgroundColor: CARD,
    borderWidth: 1,
    borderColor: BORDER,
    overflow: 'hidden',
  },

  tableHeader: {
    flexDirection: 'row-reverse',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.10)',
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  th: { color: MUTED, fontSize: 11, fontWeight: '900', textAlign: 'right' },

  tr: {
    flexDirection: 'row-reverse',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.08)',
  },
  td: { color: TEXT, fontSize: 12, fontWeight: '700', textAlign: 'right' },
  tdWrap: { justifyContent: 'center' },
  tdStrong: { color: TEXT, fontSize: 12.5, fontWeight: '900', textAlign: 'right' },
  tdMuted: { color: MUTED, fontSize: 11, fontWeight: '700', textAlign: 'right', marginTop: 2 },

  badge: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
    alignSelf: 'flex-end',
  },
  badgeOn: { backgroundColor: 'rgba(200,83,30,0.18)', borderColor: 'rgba(200,83,30,0.18)' },
  badgeOff: { backgroundColor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.16)' },
  badgeText: { color: TEXT, fontSize: 11, fontWeight: '900' },

  loadingBox: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10, padding: 16 },
  loadingText: { color: MUTED, fontWeight: '800', fontSize: 13 },
  errTitle: { color: TEXT, fontSize: 14, fontWeight: '900' },
  errText: { color: MUTED, fontSize: 12, fontWeight: '700', textAlign: 'center' },
});